import { useParams } from "react-router-dom";
import { h1Class, metaLabelClass } from "@groot/ui/typography";
import { cn } from "@groot/ui/lib/utils";
import { LoadingState } from "@groot/ui/loading-spinner";
import { useLightTheme } from "@/lib/useLightTheme";
import { RequestStatusBadge } from "@/components/status-badges";
import {
  usePhotoRequestByPublicLink,
  usePublicUploadPhoto,
  usePublicDeletePhoto,
  MAX_PHOTOS_PER_REQUEST,
} from "@/pages/photo-request/hooks/usePhotoRequests";
import { toast } from "sonner";
import { useState, useRef } from "react";
import { Upload, Trash2, CheckCircle2, Loader2 } from "lucide-react";

export function PublicPhotoUpload() {
  useLightTheme();
  const { publicLink } = useParams<{ publicLink: string }>();
  const { data: request, isLoading } = usePhotoRequestByPublicLink(publicLink || "");
  const uploadPhoto = usePublicUploadPhoto(publicLink || "");
  const deletePhoto = usePublicDeletePhoto(publicLink || "");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !request) return;

    const fileArray = Array.from(files);
    const currentCount = request.files?.length ?? 0;
    const remaining = MAX_PHOTOS_PER_REQUEST - currentCount;

    if (remaining <= 0) {
      toast.error("Photo limit reached", {
        description: `You can upload a maximum of ${MAX_PHOTOS_PER_REQUEST} photos.`,
      });
      return;
    }

    const toUpload = fileArray.length <= remaining ? fileArray : fileArray.slice(0, remaining);
    if (toUpload.length < fileArray.length) {
      toast.warning("Photo limit", {
        description: `Only ${toUpload.length} of ${fileArray.length} photo(s) uploaded — the ${MAX_PHOTOS_PER_REQUEST}-photo limit has been reached.`,
      });
    }

    setIsUploading(true);
    try {
      for (const file of toUpload) {
        await uploadPhoto.mutateAsync({
          file,
          contentType: file.type,
        });
      }
      toast.success("Uploaded", { description: "Photos uploaded successfully" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Failed to upload photos" });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePhoto = async (fileId: number) => {
    if (!request) return;
    try {
      await deletePhoto.mutateAsync({ fileId });
      toast.success("Removed", { description: "Photo removed" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Failed to remove photo" });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center">
        <LoadingState text="Loading…" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center p-4">
        <div className="bg-card rounded-2xl shadow-sm p-8 max-w-sm w-full text-center">
          <p className="font-semibold text-foreground">Link not found</p>
          <p className="text-sm text-muted-foreground mt-1">
            This photo request link is invalid or has expired.
          </p>
        </div>
      </div>
    );
  }

  const isCompleted = request.status === "completed";
  const hasPhotos = request.files && request.files.length > 0;

  return (
    <div className="min-h-screen bg-muted/50 py-8 px-4">
      <div className="max-w-[480px] mx-auto bg-card rounded-2xl shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 flex items-center gap-2.5">
          <img src="/logo.png" alt="Riqah Design" className="h-12 w-auto" />
          <span className="text-sm font-semibold text-foreground">Riqah Design</span>
        </div>

        {/* Hero */}
        <div className="px-6 py-6 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Hello{request.customer?.name ? `, ${request.customer.name}` : ""}
          </p>
          <h1 className={cn(h1Class, "mt-0.5")}>Upload Your Photos</h1>
          {request.order?.orderNumber && (
            <p className="text-xs text-muted-foreground mt-1">
              for order #{request.order.orderNumber}
            </p>
          )}
          <div className="mt-3">
            <RequestStatusBadge
              status={isCompleted ? "completed" : "pending"}
              label={isCompleted ? "Completed" : "Pending"}
            />
          </div>
        </div>

        {/* Print preview, once the studio has shared one */}
        {request.previewLink && request.previewSharedAt && !request.photosClearedAt && (
          <a
            href={`/public/print-previews/${request.previewLink}`}
            className="mx-6 mb-5 flex items-center gap-3 rounded-xl bg-primary px-4 py-3 text-sm text-primary-foreground"
          >
            <span className="text-lg" aria-hidden>
              ✨
            </span>
            <span className="flex-1">
              <span className="block font-medium">Your polaroids are ready to preview</span>
              <span className="block text-xs opacity-80">Swipe through them before we print →</span>
            </span>
          </a>
        )}

        {/* Upload zone — pending only */}
        {!isCompleted && (
          <div className="px-6 py-5 border-t border-border">
            <p className={cn(metaLabelClass, "mb-3")}>Upload Photos</p>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />

            <button
              type="button"
              onClick={() => !isUploading && fileInputRef.current?.click()}
              disabled={isUploading}
              className="w-full border-2 border-dashed border-border rounded-xl py-8 flex flex-col items-center gap-2 hover:border-foreground/30 hover:bg-muted/50 disabled:pointer-events-none"
            >
              {isUploading ? (
                <>
                  <Loader2 className="size-6 text-muted-foreground animate-spin" />
                  <span className="text-sm font-medium text-foreground">Uploading…</span>
                </>
              ) : (
                <>
                  <Upload className="size-6 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Tap to upload photos</span>
                  <span className="text-xs text-muted-foreground">
                    JPG, PNG, HEIC · up to {MAX_PHOTOS_PER_REQUEST} photos
                  </span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Photos grid */}
        {hasPhotos && (
          <div className="px-6 py-5 border-t border-border">
            <p className={cn(metaLabelClass, "mb-3")}>
              Your Photos ({request.files!.length}/{MAX_PHOTOS_PER_REQUEST})
            </p>

            <div className="grid grid-cols-2 gap-3">
              {request.files!.map((file) => (
                <div
                  key={file.id}
                  className="relative aspect-square rounded-xl overflow-hidden bg-muted"
                >
                  <img
                    src={`/api/v1/public/photo-requests/${publicLink}/files/${file.id}`}
                    alt={file.fileName}
                    className="w-full h-full object-cover"
                  />
                  {!isCompleted && (
                    <button
                      type="button"
                      onClick={() => removePhoto(file.id)}
                      disabled={deletePhoto.isPending}
                      className="absolute top-1.5 right-1.5 size-7 rounded-full bg-white/90 shadow flex items-center justify-center text-destructive hover:bg-white disabled:opacity-50"
                      aria-label="Remove photo"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed state */}
        {isCompleted && (
          <div className="px-6 py-5 border-t border-border">
            <div className="flex items-center gap-2.5 text-success">
              <CheckCircle2 className="size-5 shrink-0" />
              <p className="text-sm font-medium">Photos submitted — thank you!</p>
            </div>
            <p className="text-xs text-muted-foreground mt-1 ml-7">This request is now closed.</p>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border">
          <p className="text-xs text-muted-foreground text-center">
            © {new Date().getFullYear()} Riqah Design
          </p>
        </div>
      </div>
    </div>
  );
}
